import { Venue } from '@/components/home/types'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { PaymentData } from './payment'
import { GooglePayCredentials, ApplePayCredentials, ApplePayUser } from '@/utils/types'
import {
  VenueCustomer,
  VenueStatusClaim,
  BalloonAnimationState,
  VenueStatusTabInformation,
} from '@/components/venue-status/types'
import { VerifyStep } from '@/components/verify/types'
export type Status = 'verified' | 'not_verified'

export interface User {
  customer_id: number | null
  email: string
  first_name: string
  last_name: string
  phone: string
  token: string | null
  require_sms_verification: boolean
}

export const tabUuidAtom = atomWithStorage<string>('tab_uuid', '')

export const loadingIndicatorAtom = atom<boolean>(true)

export const sessionExpiredAtom = atom<boolean>(false)

export const statusAtom = atomWithStorage<Status>('status', 'not_verified')

export const paymentMethodsAtom = atomWithStorage<PaymentData[]>('paymentMethods', [])

export const payWithGooglePayAtom = atomWithStorage<boolean>('usingGooglePay', false)
export const payWithApplePayAtom = atomWithStorage<boolean>('usingApplePay', false)
export const canPayWithApplePayAtom = atom<boolean>(false)
export const canPayWithGooglePayAtom = atom<boolean>(false)

export const tryOpeningHostedPayentPageAtom = atom<boolean>(false)

export const userInfoAtom = atomWithStorage<User | null>('user', null)

export const venueCustomersAtom = atomWithStorage<Array<VenueCustomer>>('venue-customers', [])
export const venueCustomerAtom = atomWithStorage<VenueCustomer | null>('venue-customer', null)
export const venueStatusClaimAtom = atomWithStorage<VenueStatusClaim | null>('venue-status-claim', null)
export const VenueStatusTabInformationAtom = atomWithStorage<Array<VenueStatusTabInformation>>(
  'venue-status-tab-information',
  []
)
export const venueStatusSignupBonusPointsAtom = atomWithStorage<number | null | undefined>(
  'venue-status-signup-bonus-points',
  null
)
export const balloonAnimationStateAtom = atomWithStorage<BalloonAnimationState>(
  'balloon-animation-state',
  BalloonAnimationState.presentationAllowed
)

export const venueAtom = atom<Venue>({} as Venue)

/** Per-venue: false after venue-status welcome flow skips SMS for that venue; omitted key => verification required. */
export const clientVerificationRequiredByVenueAtom = atomWithStorage<Record<string, boolean>>(
  'client-verification-required-by-venue',
  {}
)

export const clientVerificationRequiredAtom = atom(
  (get) => {
    const venueUuid = get(venueAtom).venue_uuid
    if (!venueUuid) return true
    const byVenue = get(clientVerificationRequiredByVenueAtom)
    return byVenue[venueUuid] ?? true
  },
  (get, set, next: boolean) => {
    const venueUuid = get(venueAtom).venue_uuid
    if (!venueUuid) return
    const byVenue = get(clientVerificationRequiredByVenueAtom)
    set(clientVerificationRequiredByVenueAtom, { ...byVenue, [venueUuid]: next })
  }
)

export const fcmTokenAtom = atomWithStorage<string | null>('fcmToken', null)

export const userSubmitsOrderAtom = atom<boolean>(false)

export const verifyComponentAtom = atom<boolean>(false)
export const verifyStepAtom = atom<VerifyStep>(VerifyStep.Info)

export const orderingStatusAtom = atom(true)

export const googlePayCredentialsAtom = atom<GooglePayCredentials | null>(null)
export const applePayCredentialsAtom = atom<ApplePayCredentials | null>(null)
export const applePayMerchantSessionAtom = atom<string | null>(null)

export const applePayUserAtom = atom<ApplePayUser | null>(null)

export const suggestAppAtom = atom<boolean>(false)

export const showOffersInfoAtom = atomWithStorage<boolean>('showOffersInfo', true)
export const showOfferItemInfoAtom = atomWithStorage<boolean>('showOfferItemInfo', true)
